import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HubConnectionState } from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { from, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { NotificationType } from 'src/app/samwebDescription/samweb/notification/notificationType';
import { AppointmentsUpdated } from 'src/app/store/dashboard/appointments/appointments.actions';
import { GridFilterSettingsUpdatedByServer } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brick-filter-state/grid-filter.actions';
import { BrickGridSettingsUpdatedByServer } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brick-grids-state/brick-grids.actions';
import { GridSortSettingsUpdatedByServer } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brick-sort-state/grid-sort.actions';
import { BrickTabSettingsUpdatedByServer } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brick-tab-state/brick-tab.actions';
import { CallHistoryProgressStateMemoChangedByServer } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/call-history-browser-behaviour/call-history-browser-behaviour.actions';
import { DirectoryColumnTypes } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-helper/directory-helper.actions';
import { BrickWallChangedByServerAction } from 'src/app/store/dashboard/brick-wall/brick-instances/brick-instances.actions';
import { BrickSettingsChangedByServer } from 'src/app/store/dashboard/brick-wall/brick-settings/brick-settings.actions';
import { CustomColumnSettingsUpdated } from 'src/app/store/dashboard/custom-column-names/custom-column-names.actions';
import { DirectoryEditRestrictionsUpdated } from 'src/app/store/dashboard/directory-edit-restrictions/directory-edit-restrictions.actions';
import { PilotPanelExtendedChangedByServerAction } from 'src/app/store/dashboard/pilot-panel/pilot-panel.actions';
import {
   AgentIsBeingLoggedOutNotification,
   RequestNewOutboundCall,
   ToastUserInfoNotification,
} from 'src/app/store/notification/notification-toastr/notification-toastr.actions';
import { CallTagsUpdatedByServer } from 'src/app/store/samwin/call-tags/call-tags.actions';
import { MarkChatAsClosedByPeer, NewChatGuestStatus } from 'src/app/store/samwin/chats/chats.actions';
import { ChatId } from 'src/app/store/samwin/chats/models/chat-id';
import { CallRecordingStateUpdatedByServer } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { LineStatusValuesUpdated } from 'src/app/store/samwin/contact-states/line-status/line-status.actions';
import { MobileStatusValuesUpdated } from 'src/app/store/samwin/contact-states/mobile-status/mobile-status.actions';
import { PresenceStatusValuesUpdated } from 'src/app/store/samwin/contact-states/presence-status/presence-status.actions';
import { GlobalTemplateConfigurationsChanged } from 'src/app/store/samwin/global-configurations/global-configurations.actions';
import { ActiveAcdExtensionChanged, AvailableAcdExtensionsUpdated } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.actions';
import { AgentListUpdated } from 'src/app/store/samwin/samwin-agent-list/samwin-agent-list.actions';
import { CallListChanged } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.actions';
import { LinesChangedAction } from 'src/app/store/samwin/samwin-lines/samwin-lines.actions';
import { MessageListChanged, MessagesChanged } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.actions';
import { MaliciousCallMailConfigChanged } from 'src/app/store/samwin/samwin-malicious-call-mail-config/samwin-malicious-call-mail-config.action';
import { MessagesLinesChanged } from 'src/app/store/samwin/samwin-message-lines/samwin-message-lines.actions';
import { OutBoundSkillShortInfoChanged } from 'src/app/store/samwin/samwin-outbound-skill-shortinfo/samwin-outbound-skill-shortinfo.actions';
import {
   PermittedSkillDiversionUpdated,
   SkillConfigurationChanged,
   SkillRealtimeDataChanged,
   SkillUserConfigurationChanged,
} from 'src/app/store/samwin/samwin-skills/samwin-skills.actions';
import { UpdateUserAssistanceStates } from 'src/app/store/samwin/samwin-user-assistance-status/samwin-user-assistance-status.actions';
import { UserConfigurationChanged } from 'src/app/store/samwin/samwin-user-configuration/samwin-user-configuration.action';
import {
   SamwinAgentStateChangedByServer,
   SamwinStatusChanged,
   UserStatusInfosChangedByServer,
} from 'src/app/store/samwin/samwin-user-status/samwin-user-status.actions';
import { SamwinDeviceAddressChangedByServer } from 'src/app/store/samwin/samwin.actions';
import { AvailableUserGroupsUpdated } from 'src/app/store/samwin/user-groups/user-groups.actions';
import { Sccs1ConnectionInfoUpdated } from 'src/app/store/server-communication/sccs1-connection/sccs1-connection.actions';
import { RealTimeConnectionStatus } from 'src/app/store/server-communication/server-communication-status';
import {
   RealTimeConnectionStatusChanged,
   RealTimeConnectorStatusChanged,
   SamwinLogonErrorOccurred,
   UserSystemMonitorDataChanged,
   WebRtcCloseConnection,
   WebRtcIceCandidatesAddedByServer,
   WebRtcSdpSignalingAdded,
   WebRtcStartClientEndpoint,
} from 'src/app/store/server-communication/server-communication.actions';
import { SwitchSessionIdAction } from 'src/app/store/session/authentication/authentication.actions';
import { FeaturesChangedByServer } from 'src/app/store/session/enabled-features/enabled-features.actions';
import { SessionUpdated, UserInformationChanged } from 'src/app/store/session/session.actions';
import {
   UpdateAgentGreetingsFromServer,
   UpdateAgentGreetingWavesFromServer,
} from 'src/app/store/settings/agent-greeting-settings/agent-greeting-settings.actions';
import { SettingsChangedByServer } from 'src/app/store/settings/settings.actions';
import { ActiveAcdExtensionChangedRealTimeData } from 'src/generated/signalR/active-acd-extension-changed-real-time-data';
import { AgentGreetingRealTimeData } from 'src/generated/signalR/agent-greeting-real-time-data';
import { AgentGreetingWavesRealTimeData } from 'src/generated/signalR/agent-greeting-waves-real-time-data';
import { AgentListRealTimeData } from 'src/generated/signalR/agent-list-real-time-data';
import { AgentLogoffNotificationRealTimeData } from 'src/generated/signalR/agent-logoff-notification-real-time-data';
import { AppointmentsRealTimeData } from 'src/generated/signalR/appointments-real-time-data';
import { AvailableAcdExtensionsRealtimeData } from 'src/generated/signalR/available-acd-extensions-realtime-data';
import { AvailableUserGroupRealtimeData } from 'src/generated/signalR/available-user-group-realtime-data';
import { BrickGridSettingsRealTimeData } from 'src/generated/signalR/brick-grid-settings-real-time-data';
import { BrickTabSettingsRealTimeData } from 'src/generated/signalR/brick-tab-settings-real-time-data';
import { BrickWallRealTimeData } from 'src/generated/signalR/brick-wall-real-time-data';
import { CallHistoryProgressStateMemoChangedRealTimeData } from 'src/generated/signalR/call-history-progress-state-memo-changed-real-time-data';
import { CallRecordingStateUpdateRealTimeData } from 'src/generated/signalR/call-recording-state-update-real-time-data';
import { CallTagsUpdatedRealTimeData } from 'src/generated/signalR/call-tags-updated-real-time-data';
import { CfgGlobalTemplateRealTimeData } from 'src/generated/signalR/cfg-global-template-real-time-data';
import { ChatGuestStatusRealTimeData } from 'src/generated/signalR/chat-guest-status-real-time-data';
import { ChatWasClosedByPeerRealTimeData } from 'src/generated/signalR/chat-was-closed-by-peer-real-time-data';
import { ConnectionInfo } from 'src/generated/signalR/connection-info';
import { CustomColumnSettingsRealTimeData } from 'src/generated/signalR/custom-column-settings-real-time-data';
import { DirectoryEditRestrictionsRealTimeData } from 'src/generated/signalR/directory-edit-restrictions-real-time-data';
import { GridFilterSettingsRealTimeData } from 'src/generated/signalR/grid-filter-settings-real-time-data';
import { GridSortSettingsRealTimeData } from 'src/generated/signalR/grid-sort-settings-real-time-data';
import { LineStatesChangedRealTimeData } from 'src/generated/signalR/line-states-changed-real-time-data';
import { MaliciousCallMailConfigRealTimeData } from 'src/generated/signalR/malicious-call-mail-config-real-time-data';
import { MessagesRealTimeData } from 'src/generated/signalR/messages-real-time-data';
import { MobileStatesChangedRealTimeData } from 'src/generated/signalR/mobile-states-changed-real-time-data';
import { PresenceStatesChangedRealTimeData } from 'src/generated/signalR/presence-states-changed-real-time-data';
import { RealTimeConnectionStatusChangedRealTimeData } from 'src/generated/signalR/real-time-connection-status-changed-real-time-data';
import { RequestNewOutboundCallRealTimeData } from 'src/generated/signalR/request-new-outbound-call-real-time-data';
import { SamwinAgentStateChangedRealTimeData } from 'src/generated/signalR/samwin-agent-state-changed-real-time-data';
import { SamwinCallListChangedRealTimeData } from 'src/generated/signalR/samwin-call-list-changed-real-time-data';
import { SamwinCallStatusChangedRealTimeData } from 'src/generated/signalR/samwin-call-status-changed-real-time-data';
import { SamwinDeviceAddressChangedRealTimeData } from 'src/generated/signalR/samwin-device-address-changed-real-time-data';
import { SamwinFeatureListChangedRealTimeData } from 'src/generated/signalR/samwin-feature-list-changed-real-time-data';
import { SamwinLogonErrorReceivedRealTimeData } from 'src/generated/signalR/samwin-logon-error-received-real-time-data';
import { SamwinMessageListChangedRealTimeData } from 'src/generated/signalR/samwin-message-list-changed-real-time-data';
import { SamwinMessageStatusChangedRealTimeData } from 'src/generated/signalR/samwin-message-status-changed-real-time-data';
import { SamwinOutBoundSkillShortInfoChangedRealTimeData } from 'src/generated/signalR/samwin-out-bound-skill-short-info-changed-real-time-data';
import { SamwinPermittedSkillDiversionsRealTimeData } from 'src/generated/signalR/samwin-permitted-skill-diversions-real-time-data';
import { SamwinSkillChangedRealTimeData } from 'src/generated/signalR/samwin-skill-changed-real-time-data';
import { SamwinSkillConfigurationChangedRealTimeData } from 'src/generated/signalR/samwin-skill-configuration-changed-real-time-data';
import { SamwinSkillUserConfigurationChangedRealTimeData } from 'src/generated/signalR/samwin-skill-user-configuration-changed-real-time-data';
import { SamwinUserConfigurationChangedRealTimeData } from 'src/generated/signalR/samwin-user-configuration-changed-real-time-data';
import { SamwinUserStateChangedRealTimeData } from 'src/generated/signalR/samwin-user-state-changed-real-time-data';
import { Sccs1ConnectionInfoChangedRealTimeData } from 'src/generated/signalR/sccs1-connection-info-changed-real-time-data';
import { SessionInfoUpdateRealTimeData } from 'src/generated/signalR/session-info-update-real-time-data';
import { ToastNotificationRealtimeData } from 'src/generated/signalR/toast-notification-realtime-data';
import { UserAssistanceStatesRealTimeData } from 'src/generated/signalR/user-assistance-states-real-time-data';
import { UserBrickSettingsChangedRealTimeData } from 'src/generated/signalR/user-brick-settings-changed-real-time-data';
import { UserInformationRealTimeData } from 'src/generated/signalR/user-information-real-time-data';
import { UserSettingsChangeRealTimeData } from 'src/generated/signalR/user-settings-change-real-time-data';
import { UserStatusInfosChangedRealtimeData } from 'src/generated/signalR/user-status-infos-changed-realtime-data';
import { UserSystemMonitoringChangedRealTimeData } from 'src/generated/signalR/user-system-monitoring-changed-real-time-data';
import { WebRtcIceCandidatesAddedRealTimeData } from 'src/generated/signalR/web-rtc-ice-candidates-added-real-time-data';
import { WebRtcSdpSignalingAddedRealTimeData } from 'src/generated/signalR/web-rtc-sdp-signaling-added-real-time-data';
import { WebRtcStartClientEndpointRealTimeData } from 'src/generated/signalR/web-rtc-start-client-endpoint-real-time-data';
import { DirectoryColumnTypesRealTimeData } from '../../../generated/signalR/directory-column-types-real-time-data';
import { SessionCookieService } from '../session-cookie.service';
import { MessagePackCaseHelper } from './messagePackCaseHelper';

@Injectable({
   providedIn: 'root',
})
export class SignalrService {
   constructor(private store: Store, private sessionCookieService: SessionCookieService) {}
   private hubConnection: signalR.HubConnection;

   private lastReceivedBrickWallRealTimeData: BrickWallRealTimeData = null;
   private lastReceivedUserBrickSettingsChangedRealTimeData: UserBrickSettingsChangedRealTimeData = null;

   private static camelcaseObject<T>(object: T): T {
      return MessagePackCaseHelper.messagePackNormalizeObject(object);
   }

   public stopConnection(): void {
      if (!_.isNil(this.hubConnection)) {
         console.log('[SignalR Service] Stopping connection');
         this.hubConnection.stop();
         this.hubConnection = null;
      }
   }

   /**
    * This is workaround to ensure correct message order during logon caused by a race condition as processing @see BrickSettingsChangedByServer relies on
    * having @see BrickWallRealTimeData already received. Processing @see BrickSettingsChangedByServer requires information about the brick type to decide
    * which store is responsible for the settings. The issue mostly occurs directly during logon, although it can theoretically occur at any time when
    * receiving brick settings for a (yet) unknown brick instance. To completely solve the issue we could
    *  - Remove the requirement of knowing which type of brick the settings belong to when processing @see BrickSettingsChangedByServer.
    *    e.g. by sending this meta info along with the message or by using a single store for all brick settings (and not one related to the brick type)
    *  - Combine @see BrickWallRealTimeData and @see BrickSettingsChangedByServer into a single message.
    *    This would avoid receiving brick settings of unknown bricks. The state type responsible for the settings could then always be determined.
    */
   private ensureMessageOrder(brickWallRealTimeData: BrickWallRealTimeData, userBrickSettingsChangedRealTimeData: UserBrickSettingsChangedRealTimeData) {
      const bothMessagesAlreadyReceived = !_.isNil(this.lastReceivedBrickWallRealTimeData) && !_.isNil(this.lastReceivedUserBrickSettingsChangedRealTimeData);

      if (!_.isNil(brickWallRealTimeData)) {
         this.lastReceivedBrickWallRealTimeData = brickWallRealTimeData;
         // forward the message if we have already received both messages
         if (bothMessagesAlreadyReceived) {
            this.store.dispatch(new BrickWallChangedByServerAction(this.lastReceivedBrickWallRealTimeData.brickDefinitions));
            return;
         }
      } else if (!_.isNil(userBrickSettingsChangedRealTimeData)) {
         this.lastReceivedUserBrickSettingsChangedRealTimeData = userBrickSettingsChangedRealTimeData;
         // forward the message if we have already received both messages
         if (bothMessagesAlreadyReceived) {
            this.store.dispatch(new BrickSettingsChangedByServer(this.lastReceivedUserBrickSettingsChangedRealTimeData.brickSettings));
            return;
         }
      }

      const bothMessagesReceived = !_.isNil(this.lastReceivedBrickWallRealTimeData) && !_.isNil(this.lastReceivedUserBrickSettingsChangedRealTimeData);
      // on first time both messages have been received we dispatch them in the correct order (brick wall is required to process brick settings)
      if (!bothMessagesAlreadyReceived && bothMessagesReceived) {
         this.store.dispatch(new BrickWallChangedByServerAction(this.lastReceivedBrickWallRealTimeData.brickDefinitions));
         this.store.dispatch(new BrickSettingsChangedByServer(this.lastReceivedUserBrickSettingsChangedRealTimeData.brickSettings));
      }
   }

   public startConnection$(): Observable<void> {
      if (!_.isNil(this.hubConnection) && this.hubConnection.state === HubConnectionState.Connected) {
         console.log('[SignalR Service] Hub is already connected. Stop the connection before you restart it.');
         return;
      }
      this.store.dispatch(new RealTimeConnectorStatusChanged(RealTimeConnectionStatus.CONNECTING));

      this.hubConnection = new signalR.HubConnectionBuilder()
         .withUrl('/samwebHub')
         // .configureLogging(signalR.LogLevel.Trace)
         .withHubProtocol(new MessagePackHubProtocol())
         .build();

      const currentSessionId = this.sessionCookieService.sessionCookieValue;

      this.hubConnection.onreconnecting((error) => {
         if (!_.isNil(error)) {
            console.error('[SignalR Service] Reconnecting', error);
         } else {
            console.log('[SignalR Service] Reconnecting');
         }
      });

      this.hubConnection.onreconnected((connectionId) => {
         console.log(`[SignalR Service] Reconnected with connectionId ${connectionId}`);
      });

      this.hubConnection.onclose((error) => {
         if (!_.isNil(error)) {
            console.error('[SignalR Service] Connection closed', error);
         } else {
            console.log('[SignalR Service] Connection closed');
         }
         this.store.dispatch(new RealTimeConnectorStatusChanged(RealTimeConnectionStatus.DISCONNECTED));
      });
      this.registerHubMethods();

      // reset cached messages to ensure processing of received messages in correct order
      this.lastReceivedBrickWallRealTimeData = null;
      this.lastReceivedUserBrickSettingsChangedRealTimeData = null;

      return from(this.hubConnection.start()).pipe(
         tap(() => console.log(`[SignalR Service] SignalR Connection started with Session ${currentSessionId}`)),
         switchMap(() => this.hubConnection.invoke<ConnectionInfo>('GetConnectionInfo')),
         map((connectionInfo) => SignalrService.camelcaseObject(connectionInfo)),
         tap((connectionInfo) => {
            console.log('SignalR ConnectionInfo', connectionInfo);
            this.store.dispatch(new RealTimeConnectorStatusChanged(RealTimeConnectionStatus.CONNECTED, currentSessionId, connectionInfo));
         }),
         catchError((err) => {
            console.log('[SignalR Service] Error while starting connection: ', err);
            return this.store.dispatch(new RealTimeConnectorStatusChanged(RealTimeConnectionStatus.DISCONNECTED));
         })
      );
   }

   private sendManualHeartbeat() {
      this.hubConnection.send('Heartbeat').then(
         () => {
            console.debug('Manual heartbeat sent');
         },
         (errorReason) => {
            console.error(`Failed to send manual heartbeat (${errorReason})`, errorReason);
         }
      );
   }

   private registerHubMethods() {
      this.hubConnection.on('SayHello', (actorAddress: string) => {
         console.log(`[SignalR Service] Hello from server. Session is running on ${actorAddress}`);
      });
      this.hubConnection.on('Heartbeat', () => {
         console.debug('Manual server heartbeat received - replying');
         this.sendManualHeartbeat();
      });
      this.hubConnection.on('sessionInfoUpdated', (data: SessionInfoUpdateRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new SessionUpdated(normalizedObject.sessionInfo));
      });
      this.hubConnection.on('SwitchToNewSession', (newSessionId: string) => {
         this.store.dispatch(new SwitchSessionIdAction(newSessionId));
      });
      this.hubConnection.on('BrickWallUpdated', (data: BrickWallRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.ensureMessageOrder(normalizedObject, null);
      });
      this.hubConnection.on('ActiveAcdExtensionChanged', (data: ActiveAcdExtensionChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new ActiveAcdExtensionChanged(normalizedObject.activeAcdExtensionId));
      });
      this.hubConnection.on('SamwinUserStateChanged', (data: SamwinUserStateChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(
            new SamwinStatusChanged(normalizedObject.status, normalizedObject.endTime, normalizedObject.allowedStatusResets, normalizedObject.originator)
         );
      });
      this.hubConnection.on('SamwinCallListChanged', (data: SamwinCallListChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new CallListChanged(normalizedObject.callList.entries));
      });
      this.hubConnection.on('SamwinMessageListChanged', (data: SamwinMessageListChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new MessageListChanged(normalizedObject.messageList.entries));
      });
      this.hubConnection.on('messagesUpdate', (data: MessagesRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new MessagesChanged(normalizedObject));
      });
      this.hubConnection.on('WebRtcStartClientEndpoint', (data: WebRtcStartClientEndpointRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new WebRtcStartClientEndpoint(normalizedObject.messageCallInitiatorId, normalizedObject.iceServers));
      });
      this.hubConnection.on('WebRtcSdpSignalingAdded', (data: WebRtcSdpSignalingAddedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new WebRtcSdpSignalingAdded(normalizedObject.commandSdpOffer));
      });
      this.hubConnection.on('WebRtcIceCandidatesAdded', (data: WebRtcIceCandidatesAddedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new WebRtcIceCandidatesAddedByServer(normalizedObject.commandIceCandidates));
      });
      this.hubConnection.on('WebRtcCloseConnection', () => {
         this.store.dispatch(new WebRtcCloseConnection());
      });
      this.hubConnection.on('SamwinCallStatusChanged', (data: SamwinCallStatusChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new LinesChangedAction(normalizedObject.sourceLineStatus, normalizedObject.destinationLineStatus));
      });
      this.hubConnection.on('SamwinMessageStatusChanged', (data: SamwinMessageStatusChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new MessagesLinesChanged(normalizedObject.sourceLineStatus));
      });
      this.hubConnection.on('SamwinSkillRealtimeDataChanged', (data: SamwinSkillChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new SkillRealtimeDataChanged(normalizedObject.skillRealtimeDataForPublishing));
      });
      this.hubConnection.on('SamwinSkillConfigurationChanged', (data: SamwinSkillConfigurationChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new SkillConfigurationChanged(normalizedObject.skillConfigurationForPublishing));
      });
      this.hubConnection.on('SamwinSkillUserConfigurationChanged', (data: SamwinSkillUserConfigurationChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new SkillUserConfigurationChanged(normalizedObject.skillUserConfigurationForPublishing));
      });
      this.hubConnection.on('SamwinUserConfigurationChanged', (data: SamwinUserConfigurationChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new UserConfigurationChanged(normalizedObject.userConfigurationForPublishing));
      });
      this.hubConnection.on('SamwinOutBoundSkillShortInfoChanged', (data: SamwinOutBoundSkillShortInfoChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new OutBoundSkillShortInfoChanged(normalizedObject.outBoundSkillShortInfoForPublishing));
      });
      this.hubConnection.on('SamwinFeatureListChanged', (data: SamwinFeatureListChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new FeaturesChangedByServer(normalizedObject.enabledFeatures));
      });
      this.hubConnection.on('SamwinPermittedSkillDiversions', (data: SamwinPermittedSkillDiversionsRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new PermittedSkillDiversionUpdated(normalizedObject.permittedDiversions));
      });
      this.hubConnection.on('brickGridSettings', (data: BrickGridSettingsRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new BrickGridSettingsUpdatedByServer(normalizedObject));
      });
      this.hubConnection.on('brickTabSettings', (data: BrickTabSettingsRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new BrickTabSettingsUpdatedByServer(normalizedObject.brickTabSettings));
      });
      this.hubConnection.on('userInformationChanged', (data: UserInformationRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new UserInformationChanged(normalizedObject));
      });
      this.hubConnection.on('agentListUpdate', (data: AgentListRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new AgentListUpdated(normalizedObject.agents));
      });
      this.hubConnection.on('gridFilterSettingsUpdate', (data: GridFilterSettingsRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new GridFilterSettingsUpdatedByServer(normalizedObject));
      });
      this.hubConnection.on('gridSortSettingsUpdate', (data: GridSortSettingsRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new GridSortSettingsUpdatedByServer(normalizedObject));
      });
      this.hubConnection.on('mobileStatesChanged', (data: MobileStatesChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new MobileStatusValuesUpdated(normalizedObject.mobileStates));
      });
      this.hubConnection.on('lineStatesChanged', (data: LineStatesChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new LineStatusValuesUpdated(normalizedObject.lineStates));
      });
      this.hubConnection.on('presenceStatesChanged', (data: PresenceStatesChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new PresenceStatusValuesUpdated(normalizedObject.presenceStates));
      });
      this.hubConnection.on('acdExtensionsUpdate', (data: AvailableAcdExtensionsRealtimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new AvailableAcdExtensionsUpdated(normalizedObject.acdExtensions));
      });
      this.hubConnection.on('userSettingsChanged', (data: UserSettingsChangeRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new SettingsChangedByServer(normalizedObject.userSettings));
      });
      this.hubConnection.on('userBrickSettingsChanged', (data: UserBrickSettingsChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.ensureMessageOrder(null, normalizedObject);
      });
      this.hubConnection.on('callRecordingStateUpdate', (data: CallRecordingStateUpdateRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new CallRecordingStateUpdatedByServer(normalizedObject.callId, normalizedObject.recordingState));
      });
      this.hubConnection.on('callTagsUpdated', (data: CallTagsUpdatedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new CallTagsUpdatedByServer(normalizedObject.calls));
      });
      this.hubConnection.on('userStatusInfosChanged', (data: UserStatusInfosChangedRealtimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new UserStatusInfosChangedByServer(normalizedObject.userStatusInfos));
      });
      this.hubConnection.on('samwinDeviceAddressChanged', (data: SamwinDeviceAddressChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new SamwinDeviceAddressChangedByServer(normalizedObject.deviceAddress));
      });
      this.hubConnection.on('samwinAgentStateChanged', (data: SamwinAgentStateChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new SamwinAgentStateChangedByServer(normalizedObject.mode));
      });
      this.hubConnection.on('sccs1ConnectionInfoChanged', (data: Sccs1ConnectionInfoChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new Sccs1ConnectionInfoUpdated(normalizedObject));
      });
      this.hubConnection.on('CallHistoryProgressStateMemoChanged', (data: CallHistoryProgressStateMemoChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new CallHistoryProgressStateMemoChangedByServer(normalizedObject));
      });
      this.hubConnection.on('realTimeConnectionStatusChange', (data: RealTimeConnectionStatusChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new RealTimeConnectionStatusChanged(normalizedObject));
      });
      this.hubConnection.on('userSystemMonitoringChanged', (data: UserSystemMonitoringChangedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new UserSystemMonitorDataChanged(normalizedObject));
      });
      this.hubConnection.on('samwinLogonErrorReceived', (data: SamwinLogonErrorReceivedRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new SamwinLogonErrorOccurred(normalizedObject));
      });
      this.hubConnection.on('requestNewOutboundCall', (data: RequestNewOutboundCallRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new RequestNewOutboundCall(normalizedObject));
      });
      this.hubConnection.on('PilotPanelExtendedUpdated', (isPilotPanelExtended: boolean) => {
         this.store.dispatch(new PilotPanelExtendedChangedByServerAction(isPilotPanelExtended));
      });
      this.hubConnection.on('customColumnSettingsUpdate', (data: CustomColumnSettingsRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new CustomColumnSettingsUpdated(normalizedObject));
      });
      this.hubConnection.on('directoryColumnTypesUpdate', (data: DirectoryColumnTypesRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new DirectoryColumnTypes(normalizedObject));
      });
      this.hubConnection.on('DirectoryEditRestrictionsUpdate', (data: DirectoryEditRestrictionsRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new DirectoryEditRestrictionsUpdated(normalizedObject));
      });
      this.hubConnection.on('appointmentsUpdate', (data: AppointmentsRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new AppointmentsUpdated(normalizedObject));
      });
      this.hubConnection.on('availableUserGroupUpdate', (data: AvailableUserGroupRealtimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new AvailableUserGroupsUpdated(normalizedObject));
      });
      this.hubConnection.on('userAssistanceStatesUpdate', (data: UserAssistanceStatesRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new UpdateUserAssistanceStates(normalizedObject));
      });
      this.hubConnection.on('maliciousCallMailConfigUpdate', (data: MaliciousCallMailConfigRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new MaliciousCallMailConfigChanged(normalizedObject.maliciousCallMailConfig));
      });
      this.hubConnection.on('chatWasClosedByPeer', (data: ChatWasClosedByPeerRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         const chatId = new ChatId(normalizedObject.chatId.callId, normalizedObject.chatId.messageId);
         this.store.dispatch(new MarkChatAsClosedByPeer(chatId));
      });
      this.hubConnection.on('chatChangeGuestStatus', (data: ChatGuestStatusRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         const chatId = new ChatId(normalizedObject.chatId.callId, normalizedObject.chatId.messageId);
         this.store.dispatch(new NewChatGuestStatus(chatId, normalizedObject.guest.status));
      });
      this.hubConnection.on('agentGreetingUpdate', (data: AgentGreetingRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new UpdateAgentGreetingsFromServer(normalizedObject.agentGreetings));
      });
      this.hubConnection.on('agentGreetingWavesUpdate', (data: AgentGreetingWavesRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new UpdateAgentGreetingWavesFromServer(normalizedObject.agentGreetingWaves));
      });
      this.hubConnection.on('toastNotificationStatesUpdate', (data: ToastNotificationRealtimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(
            new ToastUserInfoNotification(
               NotificationType.getTypeByStringValue(normalizedObject.notificationType),
               normalizedObject.notificationTitle,
               normalizedObject.notificationBody,
               { positionClass: normalizedObject.notificationPosition }
            )
         );
      });
      this.hubConnection.on('globalTemplateConfigurationsChanged', (data: CfgGlobalTemplateRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new GlobalTemplateConfigurationsChanged(normalizedObject));
      });
      this.hubConnection.on('agentLogoffNotification', (data: AgentLogoffNotificationRealTimeData) => {
         const normalizedObject = SignalrService.camelcaseObject(data);
         this.store.dispatch(new AgentIsBeingLoggedOutNotification(normalizedObject.timestamp));
      });
   }
}
